<template>
  <div class="bg-white rounded-lg shadow">
    <div class="px-5 py-6 sm:px-6">
      <div class="sm:flex sm:items-center">
        <div class="sm:flex-auto">
          <h3 class="text-lg font-medium text-gray-900">Related Keywords</h3>
          <p class="mt-2 text-sm text-gray-700">
            Click on a keyword to see detailed metrics
          </p>
        </div>
      </div>

      <div class="mt-6 grid gap-6 lg:grid-cols-2">
        <div
          v-for="keyword in keywords"
          :key="keyword.term"
          class="relative border rounded-lg hover:bg-gray-50 transition-colors duration-200"
          :class="{ 'border-green-500 bg-green-50 hover:bg-green-100': keyword.isOpportunity }"
        >
          <!-- Clickable Header -->
          <div class="p-4">
            <KeywordRelationship 
              :is-seed-keyword="keyword.is_seed_keyword"
              :seed-keyword="keyword.seed_keyword"
            />
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <div class="flex items-center justify-between">
                  <h4 class="text-md font-medium text-gray-900">{{ keyword.term }}</h4>
                  <div class="flex items-center space-x-2">
                    <span v-if="keyword.isOpportunity" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Opportunity!
                    </span>
                    <!-- Intent Badges -->
                    <span 
                      v-if="keyword.searchIntent?.primary === 'commercial'" 
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                    >
                      Commercial
                    </span>
                    <span 
                      v-if="keyword.searchIntent?.primary === 'informational'" 
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                    >
                      Informational
                    </span>
                    <span 
                      v-if="keyword.searchIntent?.primary === 'navigational'" 
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      Navigational
                    </span>
                    <span 
                      v-if="keyword.searchIntent?.primary === 'transactional'" 
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                    >
                      Transactional
                    </span>
                    <!-- Chevron icon that rotates -->
                    <svg 
                      @click="toggleKeyword(keyword.term)"
                      class="w-5 h-5 text-gray-500 transform transition-transform duration-200 cursor-pointer"
                      :class="{ 'rotate-180': expandedKeywords.has(keyword.term) }"
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                    >
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </div>
                </div>
                <!-- Basic Metrics -->
                <div class="mt-2 grid grid-cols-2 gap-4">
                  <div>
                    <p class="text-sm text-gray-500">Search Volume</p>
                    <p class="text-sm font-medium text-gray-900">{{ keyword.searchVolume.toLocaleString() }}</p>
                  </div>
                  <div>
                    <p class="text-sm text-gray-500">CPC</p>
                    <p class="text-sm font-medium text-gray-900">${{ keyword.cpc.toFixed(2) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Generate Ideas Button (for all keywords) -->
            <div class="mt-3">
              <button
                @click="generateIdeas(keyword.term)"
                class="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                :disabled="generatingIdeas.has(keyword.term)"
              >
                <svg v-if="generatingIdeas.has(keyword.term)" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ generatingIdeas.has(keyword.term) ? 'Generating Ideas...' : 'Generate Content Ideas' }}
              </button>
            </div>

            <!-- Blog Ideas (if generated) -->
            <div v-if="keyword.blog_ideas?.length" class="mt-3 space-y-3">
              <h5 class="text-sm font-medium text-gray-900">Content Ideas:</h5>
              <div class="space-y-3">
                <div 
                  v-for="(idea, index) in parsedBlogIdeas(keyword.blog_ideas)" 
                  :key="index"
                  class="bg-white p-3 rounded-lg border border-gray-200 shadow-sm"
                >
                  <div class="flex items-center justify-between">
                    <h6 class="font-medium text-gray-900">{{ idea.title }}</h6>
                    <div class="flex items-center space-x-2">
                      <span class="text-xs font-medium px-2 py-1 rounded-full" 
                        :class="{
                          'bg-blue-100 text-blue-800': idea.contentType === 'how-to',
                          'bg-green-100 text-green-800': idea.contentType === 'listicle',
                          'bg-purple-100 text-purple-800': idea.contentType === 'tutorial',
                          'bg-yellow-100 text-yellow-800': idea.contentType === 'comparison',
                          'bg-red-100 text-red-800': idea.contentType === 'case-study',
                          'bg-indigo-100 text-indigo-800': idea.contentType === 'evergreen',
                          'bg-pink-100 text-pink-800': idea.contentType === 'news',
                          'bg-orange-100 text-orange-800': idea.contentType === 'review',
                          'bg-gray-100 text-gray-800': !idea.contentType || idea.contentType === 'general'
                        }"
                      >
                        {{ idea.contentType || 'General' }}
                      </span>
                      <button
                        @click="writeArticle({ keyword, title: idea })"
                        class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Write Article
                      </button>
                    </div>
                  </div>
                  <p class="mt-1 text-sm text-gray-600">{{ idea.description }}</p>
                  <div class="mt-2">
                    <h6 class="text-xs font-medium text-gray-700">Key Points:</h6>
                    <ul class="mt-1 list-disc list-inside text-sm text-gray-600">
                      <li v-for="(point, pointIndex) in idea.keyPoints" :key="pointIndex">
                        {{ point }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Expandable Content -->
          <div 
            v-show="expandedKeywords.has(keyword.term)"
            class="border-t px-4 py-3 space-y-4"
          >
            <!-- Difficulty Metrics -->
            <div class="space-y-3">
              <div>
                <div class="flex justify-between mb-1">
                  <span class="text-sm text-gray-500">Competition Level</span>
                  <span class="text-sm font-medium" :class="{
                    'text-green-600': keyword.difficulty.competitionLevelText === 'LOW',
                    'text-yellow-600': keyword.difficulty.competitionLevelText === 'MEDIUM',
                    'text-red-600': keyword.difficulty.competitionLevelText === 'HIGH'
                  }">{{ keyword.difficulty.competitionLevelText }}</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="h-2 rounded-full" :class="{
                    'bg-green-600': keyword.difficulty.competitionLevelText === 'LOW',
                    'bg-yellow-600': keyword.difficulty.competitionLevelText === 'MEDIUM',
                    'bg-red-600': keyword.difficulty.competitionLevelText === 'HIGH'
                  }" :style="{ width: `${keyword.difficulty.competitionLevel * 33.33}%` }"></div>
                </div>
              </div>

              <div v-if="keyword.difficulty.competition > 0">
                <div class="flex justify-between mb-1">
                  <span class="text-sm text-gray-500">Competition Score</span>
                  <span class="text-sm font-medium">{{ (keyword.difficulty.competition * 100).toFixed(1) }}/100</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-blue-600 h-2 rounded-full" :style="{ width: `${keyword.difficulty.competition * 100}%` }"></div>
                </div>
              </div>

              <div v-if="keyword.difficulty.keywordDifficulty > 0">
                <div class="flex justify-between mb-1">
                  <span class="text-sm text-gray-500">Keyword Difficulty</span>
                  <span class="text-sm font-medium" :class="{
                    'text-green-600': keyword.difficulty.keywordDifficulty <= 30,
                    'text-yellow-600': keyword.difficulty.keywordDifficulty > 30 && keyword.difficulty.keywordDifficulty <= 70,
                    'text-red-600': keyword.difficulty.keywordDifficulty > 70
                  }">
                    {{ keyword.difficulty.keywordDifficulty }}/100
                  </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="h-2 rounded-full" :class="{
                    'bg-green-600': keyword.difficulty.keywordDifficulty <= 30,
                    'bg-yellow-600': keyword.difficulty.keywordDifficulty > 30 && keyword.difficulty.keywordDifficulty <= 70,
                    'bg-red-600': keyword.difficulty.keywordDifficulty > 70
                  }" :style="{ width: `${keyword.difficulty.keywordDifficulty}%` }"></div>
                </div>
              </div>
            </div>

            <!-- Search Intent -->
            <div class="space-y-3">
              <h5 class="text-sm font-medium text-gray-700">Search Intent</h5>
              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <span class="text-sm font-medium" :class="{
                    'text-yellow-700': keyword.searchIntent?.primary === 'commercial',
                    'text-purple-700': keyword.searchIntent?.primary === 'informational',
                    'text-blue-700': keyword.searchIntent?.primary === 'navigational',
                    'text-green-700': keyword.searchIntent?.primary === 'transactional'
                  }">
                    Primary Intent: {{ keyword.searchIntent?.primary || 'Unknown' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Search Trends -->
            <div class="flex justify-between items-center text-sm pt-2">
              <div class="flex items-center space-x-2">
                <span class="text-gray-500">Monthly Trend:</span>
                <span :class="{
                  'text-green-600': keyword.searchTrends.monthly > 0,
                  'text-red-600': keyword.searchTrends.monthly < 0,
                  'text-gray-600': keyword.searchTrends.monthly === 0
                }">
                  {{ keyword.searchTrends.monthly > 0 ? '+' : '' }}{{ keyword.searchTrends.monthly }}%
                </span>
              </div>
              <div class="flex items-center space-x-2">
                <span class="text-gray-500">Yearly:</span>
                <span :class="{
                  'text-green-600': keyword.searchTrends.yearly > 0,
                  'text-red-600': keyword.searchTrends.yearly < 0,
                  'text-gray-600': keyword.searchTrends.yearly === 0
                }">
                  {{ keyword.searchTrends.yearly > 0 ? '+' : '' }}{{ keyword.searchTrends.yearly }}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <BlogIdeasPopup
    :show="showIdeasPopup"
    :keyword="selectedKeyword"
    :ideas="blog_ideas"
    :loading="loading"
    @close="closeIdeasPopup"
    @generate="handleGenerateIdeas"
  />
</template>

<script setup>
import { ref } from 'vue'
import { useKeywordStore } from '../stores/keyword'
import { useOpenAIStore } from '../stores/openai'
import KeywordRelationship from './KeywordRelationship.vue'
import BlogIdeasPopup from './BlogIdeasPopup.vue'

const props = defineProps({
  keywords: {
    type: Array,
    required: true
  }
})

const keywordStore = useKeywordStore()
const openAIStore = useOpenAIStore()
const expandedKeywords = ref(new Set())
const generatingIdeas = ref(new Set())
const showIdeasPopup = ref(false)
const selectedKeyword = ref('')
const blog_ideas = ref([])
const loading = ref(false)

const toggleKeyword = (term) => {
  const newSet = new Set(expandedKeywords.value)
  if (newSet.has(term)) {
    newSet.delete(term)
  } else {
    newSet.add(term)
  }
  expandedKeywords.value = newSet
}

async function generateIdeas(keywordTerm) {
  try {
    loading.value = true
    selectedKeyword.value = keywordTerm
    showIdeasPopup.value = true
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error generating ideas:', error)
    }
    throw error
  } finally {
    loading.value = false
  }
}

const handleGenerateIdeas = async ({ keyword, contentType }) => {
  try {
    loading.value = true
    // Find the full keyword object
    const keywordObj = props.keywords.find(k => k.term === keyword)
    if (!keywordObj) throw new Error('Keyword not found')
    
    // Ensure we have location_code and language_code
    if (!keywordObj.location_code) {
      throw new Error('Location code is missing from keyword')
    }

    const ideas = await keywordStore.generateIdeas(
      keywordObj, 
      contentType,
      keywordObj.language_code || 'en'  // Use keyword's language code or default to 'en'
    )
    
    blog_ideas.value = ideas.map(idea => {
      if (typeof idea === 'string') {
        try {
          return JSON.parse(idea)
        } catch (e) {
          return { 
            title: idea, 
            description: '', 
            keyPoints: [],
            contentType: idea.contentType || contentType || 'general',
            searchIntent: idea.searchIntent || keywordObj.searchIntent?.type || 'informational',
            language_code: keywordObj.language_code || 'en'
          }
        }
      }
      return {
        ...idea,
        language_code: keywordObj.language_code || 'en'
      }
    })
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error generating ideas:', error)
    }
    throw error
  } finally {
    loading.value = false
  }
}

function closeIdeasPopup() {
  showIdeasPopup.value = false
  selectedKeyword.value = ''
  blog_ideas.value = []
}

function parsedBlogIdeas(blog_ideas) {
  if (!Array.isArray(blog_ideas)) return []
  
  return blog_ideas.map(idea => {
    try {
      if (typeof idea === 'string') {
        return JSON.parse(idea)
      }
      return idea
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to parse blog idea:', error)
      }
      throw error
    }
  })
}

async function writeArticle({ keyword, title }) {
  try {
    await openAIStore.writeArticle({
      keyword: keyword.term,
      title: title.title,
      keyPoints: title.keyPoints || [],
      searchVolume: keyword.searchVolume,
      competition: keyword.difficulty?.competition || 0,
      competitionLevel: keyword.difficulty?.competitionLevelText || 'Unknown',
      seed_keyword: keyword.seed_keyword || keyword.term,
      language_code: keyword.language_code || 'en'
    })
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to write article:', error)
    }
    throw error
  }
}
</script>

<style scoped>
.difficulty-easy {
  @apply text-green-600 dark:text-green-400;
}

.difficulty-medium {
  @apply text-yellow-600 dark:text-yellow-400;
}

.difficulty-hard {
  @apply text-red-600 dark:text-red-400;
}
</style>


